import { isEnabledFlag } from "./featureFlags";

// V1's custom copy replaces ASAP estimates, never scheduled/preordered dates.
export const getOnDemandCustomText = ({ settings = {}, branch, type = "delivery", cart = {}, language = "english" } = {}) => {
  if (cart.preordering_max_leadtime > 0 && cart.preordered_items > 0) return "";
  const pickup = type === "pickup";
  const flag = pickup ? "enable_on_demand_custom_string_pickup" : "enable_on_demand_custom_string";
  const key = pickup ? "on_demand_pickup_custom_string" : "on_demand_custom_string";
  const owner = settings.is_branch_based_ordering && branch && branch[flag] !== undefined ? branch : settings;
  if (!isEnabledFlag(owner[flag])) return "";
  const ar = language === "arabic";
  return String(owner[`${key}${ar ? "_ar" : ""}`] || "").trim() ||
    String(owner[`${key}${ar ? "" : "_ar"}`] || "").trim() ||
    (ar ? "في أقرب وقت" : "ASAP");
};

// These are store-local calendar dates/times, not UTC instants. Parsing a
// YYYY-MM-DD as UTC moves the chosen delivery day for some shoppers.
export const formatCheckoutDate = (value, arabic = false, now = new Date()) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(`${value || ""}`)) return "";
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  const [year, month, day] = value.split("-").map(Number);
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) return "";
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (parsed.getTime() === today.getTime()) return arabic ? "اليوم" : "Today";
  if (parsed.getTime() === tomorrow.getTime()) return arabic ? "غداً" : "Tomorrow";
  return new Intl.DateTimeFormat(arabic ? "ar-KW" : "en-GB", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
  }).format(parsed);
};

export const formatCheckoutTime = (value, arabic = false) => {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(`${value || ""}`);
  if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) return "";
  return new Intl.DateTimeFormat(arabic ? "ar-KW" : "en-US", {
    hour: "numeric", minute: "2-digit", hour12: true,
  }).format(new Date(2000, 0, 1, Number(match[1]), Number(match[2])));
};

export const formatShortFulfillmentDate = (date, language = "english", now = new Date()) => {
  if (!formatCheckoutDate(date, language === "arabic", now)) return "";
  const parsed = new Date(`${date}T12:00:00`);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (parsed.getTime() === today.getTime()) return language === "arabic" ? "اليوم" : "Today";
  if (parsed.getTime() === tomorrow.getTime()) return language === "arabic" ? "غداً" : "Tomorrow";
  return new Intl.DateTimeFormat(language === "arabic" ? "ar-KW" : "en-US", {
    weekday: "short", month: "short", day: "numeric",
  }).format(parsed);
};

// Number(null), Number("") and Number(false) are all zero, but none is an ETA.
export const getAsapWithinMinutes = (time = {}) => {
  const raw = time.withinMinutes ?? time.within;
  if ((typeof raw !== "number" && typeof raw !== "string") || `${raw}`.trim() === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
};

export const formatAsapTimeParts = (time = {}, language = "english", now = new Date(), presentation = {}) => {
  const custom = getOnDemandCustomText({ ...presentation, language });
  if (custom) return [custom];
  const arabic = language === "arabic";
  const minutes = getAsapWithinMinutes(time);
  if (minutes !== null) {
    const rounded = Math.round(minutes);
    if (rounded === 0) return [arabic ? "جاهز الآن" : "Ready now"];
    if (rounded < 60) return [arabic ? `خلال ${rounded} دقيقة` : `Within ${rounded} min`];
    const hours = Math.floor(rounded / 60);
    const remaining = rounded % 60;
    if (arabic) return [remaining ? `خلال ${hours} س ${remaining} د` : `خلال ${hours} س`];
    return [remaining ? `Within ${hours} hr ${remaining} min` : `Within ${hours} ${hours === 1 ? "hour" : "hours"}`];
  }
  const day = formatShortFulfillmentDate(time.date, language, now);
  const label = day || (arabic ? "في أقرب وقت" : "ASAP");
  // Format the authoritative store-local window, without inventing an elapsed ETA.
  const start = formatCheckoutTime(time.start, arabic);
  const end = formatCheckoutTime(time.end, arabic);
  return start && end && start !== end ? [label, `${start} – ${end}`] : [label];
};

export const formatAsapTimeSummary = (...args) => formatAsapTimeParts(...args).join(" · ");

export const formatCheckoutTimeSummary = (time = {}, arabic = false, now, presentation = {}) => {
  const date = formatCheckoutDate(time.date, arabic, now);
  if (time.type === "asap") return formatAsapTimeSummary(time, arabic ? "arabic" : "english", now, presentation);
  const window = [formatCheckoutTime(time.start, arabic), formatCheckoutTime(time.end, arabic)]
    .filter(Boolean).join(" – ");
  return [date, window].filter(Boolean).join(" · ");
};
